import Modal from "../../../../components/common/Modal";
import PrimaryButton from "../../../../components/common/PrimaryButton";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import styles from "./styles.module.scss";
import { fetchAndActivate, getValue } from "firebase/remote-config";
import { useState, useEffect, useRef } from "react";
import z from "zod";
import { remoteConfig } from "../../../../firebase/firebase";
import {
  isValidSaudiPhoneNumber,
  normalizeSaudiNumber,
} from "../../../../setup/util/phoneNumber.helpers";
import { RemoteConfig } from "../../../../ampli/constants";
import { Inputs } from "../addPartyModal";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ESenderRole } from "../../../../setup/api/services/contract/models";

interface IBulkUploadModalProps {
  handleClose: () => void;
  handleSave: (partyData: Inputs[], errors?: PartyErrorsMap) => void;
  modalOpen: boolean;
  type: string;
}

export interface IPartyError {
  field: string;
  message: string;
}

export type PartyErrorsMap = Record<string, Record<string, string>>;

type Row = {
  name: string;
  phoneNumber: string;
  amount: number;
  fee?: number;
  role?: string;
  id?: string;
  rowId?: string;
  isCompany?: boolean;
  companyUnifiedNumber?: string;
  companyName?: string;
  iban?: string;
  nationalAddress?: string;
};

const BulkUploadModal = ({
  handleClose,
  modalOpen,
  handleSave,
  type,
}: IBulkUploadModalProps) => {
  const { t } = useTranslation();
  const { watch } = useFormContext();
  const [parseError, setParseError] = useState("");
  const [parsedFile, setParsedFile] = useState<File>();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [validEntries, setValidEntries] = useState<Inputs[]>([]);
  const [partyErrors, setPartyErrors] = useState<PartyErrorsMap>({});
  const [maxPrice, setMaxPrice] = useState(1000000);

  useEffect(() => {
    const fetchFirebaseConfig = async () => {
      await fetchAndActivate(remoteConfig);
      const maxPriceValue = getValue(
        remoteConfig,
        "max_product_price"
      ) as unknown as RemoteConfig;
      setMaxPrice(+maxPriceValue?._value);
    };
    fetchFirebaseConfig();
  }, []);

  const schema: z.ZodType<Row> = z
    .object({
      name: z.string(),
      phoneNumber: z.string(),
      amount: z.number(),
      fee: z.number().optional(),
      role: z.string().optional(),
      id: z.string().optional(),
      rowId: z.string().optional(),
      isCompany: z.boolean().optional(),
      companyUnifiedNumber: z.string().optional(),
      companyName: z.string().optional(),
      iban: z.string().optional(),
      nationalAddress: z.string().optional(),
    })
    .refine((data) => type !== "provider" || !!data.role, {
      path: ["role"],
      message: t("err_role_required_for_providers"),
    })
    .superRefine((data, ctx) => {
      const isBroker =
        data.role === "broker" || data.role === ESenderRole?.BROKER;

      if (!isBroker) {
        // name always required
        if (!data.name || data.name.trim().length < 5) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["name"],
            message: t("err_min_chars", { min: 5 }),
          });
        } else if (data.name.trim().length > 100) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["name"],
            message: t("err_max_chars", { max: 100 }),
          });
        }

        if (!isValidSaudiPhoneNumber(data.phoneNumber)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["phoneNumber"],
            message: t("err_invalid_phone"),
          });
        }

        if (data.companyUnifiedNumber) {
          if (!data.companyName || data.companyName.trim().length < 5) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["companyName"],
              message: t("err_min_chars", { min: 5 }),
            });
          }
          if (data.companyUnifiedNumber.trim().length < 3) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["companyUnifiedNumber"],
              message: t("err_required"),
            });
          }
        }

        if (type === "customer" ? data.amount < 1 : data.amount < 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["amount"],
            message: t("err_number_min", { min: type === "customer" ? 1 : 0 }),
          });
        }
        if (data.amount > maxPrice) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["amount"],
            message: t("err_number_max", { max: maxPrice }),
          });
        }
      }
    });

  const partiesSchema = z.array(schema);

  // Returns the dedup key for a row: phoneNumber
  const rowDedupKey = (row: Row) => row.phoneNumber || "";

  // Helpers for duplicate checking
  const checkInternalDuplicates = (parsedData: Row[]) => {
    const keys = new Map<string, number[]>();
    parsedData.forEach((row, index) => {
      const key = rowDedupKey(row);
      if (!key) return;
      if (!keys.has(key)) keys.set(key, []);
      keys.get(key)?.push(index);
    });
    return keys;
  };

  const checkExternalDuplicates = (parsedData: Row[]) => {
    const currentMilestoneIndex = watch("currentMilestoneIndex");
    const existingParties =
      watch(`milestones.${currentMilestoneIndex}.parties`) || [];
    const existingPhoneNumbers = new Set(
      existingParties.map((p: any) => p.phoneNumber).filter(Boolean)
    );
    return parsedData.map((row) => ({
      key: rowDedupKey(row),
      isDuplicate: existingPhoneNumbers.has(row.phoneNumber),
    }));
  };

  // Core validation logic
  const processAndValidateData = (parsedData: Row[], file: File) => {
    const errorsMap: PartyErrorsMap = {};
    const valid: Inputs[] = [];

    // Validate each row against schema first
    parsedData.forEach((row, index) => {
      const rowId = row.rowId || `${type}-${index}-${Date.now()}`;
      try {
        schema.parse(row);
      } catch (err) {
        if (err instanceof z.ZodError) {
          errorsMap[rowId] = {};
          err.issues.forEach((issue) => {
            const field = (issue.path[0] as string) || "general";
            errorsMap[rowId][field] = issue.message;
          });
        }
      }
      const uniqueSalt = Math.random().toString(36).substring(2, 9);
      valid.push({
        ...row,
        id: `${type}-${index}-${Date.now()}-${uniqueSalt}`,
        rowId,
        type,
      });
    });

    // Handle internal duplicates - only mark duplicated parties (not the first instance)
    const internalDuplicates = checkInternalDuplicates(parsedData);
    const internalDuplicateGroups: Record<string, Row[]> = {};

    // Group parties by phone number where there are duplicates
    internalDuplicates.forEach((indices, phoneNumber) => {
      if (indices.length > 1) {
        // Sort indices to ensure consistent ordering
        const sortedIndices = [...indices].sort((a, b) => a - b);
        internalDuplicateGroups[phoneNumber] = sortedIndices.map(
          (idx) => parsedData[idx]
        );
      }
    });

    // Mark only duplicate entries after the first one with errors
    Object.values(internalDuplicateGroups).forEach((duplicateRows) => {
      if (duplicateRows.length > 1) {
        const duplicatesToMark = duplicateRows.slice(1);
        duplicatesToMark.forEach((row) => {
          const index = parsedData.indexOf(row);
          if (index !== -1 && index < valid.length) {
            const rowId = valid[index].rowId;
            if (rowId) {
              const errorField = "phoneNumber";
              const errorMsg = t("err_duplicate_phone");
              errorsMap[rowId] = {
                ...(errorsMap[rowId] || {}),
                [errorField]: errorMsg,
              };
            }
          }
        });
      }
    });

    // Handle external duplicates - mark all entries that match existing parties
    const externalDuplicates = checkExternalDuplicates(parsedData);
    externalDuplicates.forEach((dup, index) => {
      if (dup.isDuplicate && index < valid.length) {
        const row = parsedData[index];
        const rowId = valid[index].rowId;
        if (rowId) {
          const errorField = "phoneNumber";
          const errorMsg = t("err_duplicate_phone");
          errorsMap[rowId] = {
            ...(errorsMap[rowId] || {}),
            [errorField]: errorMsg,
          };
        }
      }
    });

    console.log("✅ Valid:", valid);
    console.log("❌ Errors:", errorsMap);

    setParsedFile(file);
    setValidEntries(valid);
    setPartyErrors(Object.keys(errorsMap).length > 0 ? errorsMap : {});
  };
  // File parsing handlers
  const handleFileChange = (files: FileList | null) => {
    setParseError("");
    setValidEntries([]);
    setPartyErrors({});
    if (inputRef.current) inputRef.current.files = files;
    if (!files?.length) return;

    const file = files[0];
    const fileType = file.name.split(".").pop()?.toLowerCase();

    if (fileType === "xlsx" || fileType === "xls") {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: "binary" });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(sheet);

          if (jsonData.length === 0) {
            setParseError(t("err_empty_file"));
            return;
          }

          const parsedData = jsonData.map((record: any, index: number) => {
            const rRole = record?.role || "";
            const uniqueSalt = Math.random().toString(36).substring(2, 9);
            const id = `${type}-${index}-${Date.now()}-${uniqueSalt}`;
            const rowId = `${type}-${index}-${Date.now()}-${uniqueSalt}`;
            return {
              ...record,
              amount: +(record.amount || 0),
              fee: 0,
              id: id,
              phoneNumber:
                rRole === "broker" || rRole === ESenderRole?.BROKER
                  ? `${record?.phoneNumber}`
                  : normalizeSaudiNumber(`${record?.phoneNumber}`),
              rowId: rowId,
              ...(record?.companyUnifiedNumber ? { isCompany: true } : {}),
            };
          }) as Row[];

          processAndValidateData(parsedData, file);
        } catch (error) {
          console.error("Error parsing XLSX:", error);
          setParseError(t("err_invalid_file_format"));
        }
      };
      reader.onerror = () => setParseError(t("err_reading_file"));
      reader.readAsBinaryString(file);
    } else {
      Papa.parse(file, {
        skipEmptyLines: true,
        header: true,
        complete: (results) => {
          const parsedData = results.data.map((record: any, index: number) => {
            const rRole = record?.role || "";
            const uniqueSalt = Math.random().toString(36).substring(2, 9);
            const id = `${type}-${index}-${Date.now()}-${uniqueSalt}`;
            const rowId = `${type}-${index}-${Date.now()}-${uniqueSalt}`;
            return {
              ...record,
              amount: +(record.amount || 0),
              fee: 0,
              phoneNumber:
                rRole === "broker" || rRole === ESenderRole?.BROKER
                  ? `${record?.phoneNumber}`
                  : normalizeSaudiNumber(`${record?.phoneNumber}`),
              id: id,
              rowId: rowId,
              ...(record?.companyUnifiedNumber ? { isCompany: true } : {}),
            };
          }) as Row[];
          processAndValidateData(parsedData, file);
        },
        error: (error) => {
          console.error("Error parsing CSV:", error);
          setParseError(t("err_invalid_file_format"));
        },
      });
    }
  };

  const onDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    handleFileChange(e.dataTransfer.files);
  };

  return (
    <Modal
      customModalContainerClassName={styles.modalContainer}
      customModalClassName={styles.modal}
      visible={modalOpen}
      onCancel={() => {
        setParsedFile(undefined);
        setParseError("");
        setValidEntries([]);
        setPartyErrors({});
        handleClose();
      }}>
      <div className={styles.container}>
        <h1 className={styles.title}>{t("lbl_upload_csv_file")}</h1>
        <input
          ref={inputRef}
          id="file-upload"
          type="file"
          accept=".csv, .xlsx, .xls"
          onChange={(e) => handleFileChange(e.target.files)}
          className={styles.hiddenInput}
        />
        <label
          htmlFor="file-upload"
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          className={styles.dropzone}>
          <img src="/assets/images/svg/uploadAlt.svg" alt="upload" />
          <p className={styles.dropzoneText}>
            {parsedFile
              ? `${parsedFile.name} ${t("lbl_contains_records", {
                  count: validEntries.length,
                })}`
              : t("lbl_drop_or_click_to_upload")}
          </p>
        </label>

        {parseError && <p className={styles.errorText}>{parseError}</p>}
        <p className={styles.noteText}>{t("lbl_accepted_formats_csv_xlsx")}</p>

        <div className={styles.divider} />
        <p className={styles.helperTitle}>
          {t("lbl_no_file_use_template_below")}
        </p>

        <a
          href={
            type === "customer"
              ? "/Add Customer Party Template.csv"
              : "/Add Provider Party Template.csv"
          }
          download
          className={styles.templateLink}>
          <img src="/assets/images/svg/csvFile.svg" alt="template" />
          <p className={styles.templateText}>{t("lbl_download_template")}</p>
        </a>

        <div className={styles.actionsRow}>
          <PrimaryButton
            style={{ background: "#0D44AD", flex: 2 }}
            onClick={() => {
              handleSave(
                validEntries,
                Object.keys(partyErrors).length > 0 ? partyErrors : {}
              );
              setParsedFile(undefined);
              setParseError("");
              setValidEntries([]);
              setPartyErrors({});
            }}>
            {t("lbl_continue")}
          </PrimaryButton>

          <PrimaryButton
            style={{ background: "#F3F6FC", color: "#0D44AD", flex: 1 }}
            onClick={handleClose}>
            {t("lbl_cancel")}
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  );
};

export default BulkUploadModal;
